import { useState } from 'react';
import { addGroupWithImage, addGroup, addGroupWithHeader } from '../../../apiServices/groupApi';
import { putToStorageDefault } from '../../../apiServices/storageApiService';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { Input } from '../../ui/Input';
import ImageSection from '../setPictureModal/ImagePicker';
import { getLowerCaseFileExtensionInFile, getValueFromUserAttributes } from '../../../util/UtilFunctions';
import { pdfToPng } from '../setPictureModal/SetPictureModalUtilFunctions';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserInfo = any;

interface PictureProcess {
  status: string;
  data?: { picture?: File | Blob; croppedImage?: string; pdfFile?: File };
}

interface CreateWorkspaceModalProps {
  closeModal: () => void;
  updateIsQuerying: (q: boolean) => void;
  currentUserInfo: UserInfo;
  useCase?: string;
  packageType?: string;
  updateGroupsList?: (...args: unknown[]) => void;
  updateCurrentUserInfo?: () => void;
  refreshList?: (...args: unknown[]) => void;
  // Legacy props from AccountSettingsWorkspace
  currentUserGroup?: string;
  groupsList?: unknown[];
  roleInCurrentWorkspace?: string;
}

export default function CreateWorkspaceModal({
  closeModal,
  updateIsQuerying,
  currentUserInfo,
  useCase,
  updateGroupsList,
  updateCurrentUserInfo,
  refreshList,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pictureProcess, setPictureProcess] = useState<PictureProcess>({ status: 'initial' });
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | undefined>();
  const { notify } = useNotification();

  const addWorkspaceWithImage = async () => {
    try {
      updateIsQuerying(true);
      const storageRes = await putToStorageDefault(pictureProcess.data!.picture!, '');
      updateIsQuerying(false);
      try {
        updateIsQuerying(true);
        const res = await addGroupWithImage(name, currentUserInfo.cognitoAttributes.Username, storageRes.key);
        updateIsQuerying(false);
        updateGroupsList?.(res.response, 0);
        closeModal();
        notify({ type: 'success', message: `Created workspace ${name}` });
      } catch {
        notify({ type: 'error', message: 'Could not create workspace' });
        updateIsQuerying(false);
        closeModal();
      }
    } catch {
      notify({ type: 'error', message: 'Could not create workspace' });
      updateIsQuerying(false);
      closeModal();
    }
  };

  const addWorkspaceHandler = async () => {
    try {
      updateIsQuerying(true);
      let res;
      if (useCase === 'root') {
        const attrs = currentUserInfo.cognitoAttributes.UserAttributes;
        const firstname = getValueFromUserAttributes(attrs, 'given_name');
        const lastname = getValueFromUserAttributes(attrs, 'family_name');
        const email = getValueFromUserAttributes(attrs, 'email');
        res = await addGroupWithHeader(name, currentUserInfo.cognitoAttributes.Username, undefined, firstname, lastname, email);
      } else {
        res = await addGroup(name, currentUserInfo.cognitoAttributes.Username);
      }
      updateIsQuerying(false);
      if (res.response?.includes?.('ERROR')) {
        notify({ type: 'error', message: `Could not create workspace ${name}` });
      } else {
        notify({ type: 'success', message: `Created workspace ${name}` });
      }
      updateGroupsList?.();
      updateCurrentUserInfo?.();
      refreshList?.();
      closeModal();
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not create workspace' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { notify({ type: 'info', message: 'Form is not complete' }); return; }
    if (name.length >= 50) { notify({ type: 'info', message: "Name can't be over 50 characters" }); return; }
    setSubmitting(true);
    if (pictureProcess.status === 'pictureCropped' || pictureProcess.status === 'pdfSelected') {
      await addWorkspaceWithImage();
    } else {
      await addWorkspaceHandler();
    }
    setSubmitting(false);
  };

  const handlePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = getLowerCaseFileExtensionInFile(event.target.files![0]);
    const mime = file.type.split('/');
    if (mime[1] === 'gif') {
      notify({ type: 'info', message: ".gif files can't be set as cover images" });
    } else if (mime[0] === 'image') {
      setPictureProcess({ status: 'pictureSelected', data: { picture: file, croppedImage: undefined } });
    } else if (mime[1] === 'pdf') {
      setPictureProcess({ status: 'pdfSelected', data: { pdfFile: file, picture: undefined } });
      try {
        const newFile = await pdfToPng(file);
        setPictureProcess({ status: 'pdfSelected', data: { picture: newFile } });
        setCroppedImageUrl(URL.createObjectURL(file));
      } catch {
        notify({ type: 'error', message: 'Could not produce pdf thumbnail' });
      }
    } else {
      notify({ type: 'info', message: 'Given file is not an image or pdf' });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        placeholder={useCase === 'root' ? 'Company name' : 'Workspace name'}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      {useCase === 'root' && <p className="text-sm text-muted-foreground">Yemma is free to use for 14 days</p>}
      {useCase !== 'root' && (
        <ImageSection
          useCase="createNew"
          pictureUploadingProcess={pictureProcess}
          currentUserInfo={currentUserInfo}
          updateCroppedImage={(img: string) => setPictureProcess({ status: 'pictureCropped', data: { picture: pictureProcess.data?.picture, croppedImage: img } })}
          croppedImageUrl={croppedImageUrl}
          handlePictureChange={handlePictureChange}
        />
      )}
      <Button type="submit" disabled={submitting}>
        {submitting ? <Spinner size="sm" /> : 'Create'}
      </Button>
    </form>
  );
}
